import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Loader2, Check, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { storage } from '../services/storage';
import { API_BASE_URL } from '../utils/api';

interface GoalPipelineProgressProps {
  goalId: string;
  goalText?: string;
  onDismiss?: () => void;
}

interface PhaseDetail {
  phase: number;
  title: string;
  status: string;
  started_at: string | null;
  completed_at: string | null;
  estimated_duration_seconds: number;
  actual_duration_seconds: number | null;
  metadata: Record<string, any>;
}

interface PipelineStatusResponse {
  goal_id: string;
  pipeline_status: string;
  pipeline_started_at: string;
  pipeline_completed_at: string | null;
  total_duration_seconds: number | null;
  phases: PhaseDetail[];
}

type SegmentState = 'pending' | 'active' | 'completed' | 'failed';

// Default status labels — overridden by backend phase titles when available
const SEGMENT_LABELS_DEFAULT = [
  'Understanding your goal',
  'Developing your plan',
  'Creating your week',
];

/**
 * Deterministically clean goal text for a smooth title.
 * "Creating your plan for run a 5K" → "Creating your plan for running a 5K"
 * Strips common prefixes like "I want to", "I'd like to", etc.
 */
function cleanGoalText(raw: string): string {
  let text = raw.trim();

  // Strip leading prefixes
  const prefixes = [
    /^i('d| would) (like|love|want) to\s+/i,
    /^i (want|need|plan) to\s+/i,
    /^my goal is to\s+/i,
    /^my goal is\s+/i,
    /^i'm (trying|looking|hoping) to\s+/i,
    /^i am (trying|looking|hoping) to\s+/i,
    /^help me\s+/i,
    /^i (just )?want\s+/i,
  ];
  for (const prefix of prefixes) {
    text = text.replace(prefix, '');
  }

  // Lowercase first char (since it follows "for")
  if (text.length > 0) {
    text = text[0].toLowerCase() + text.slice(1);
  }

  // Strip trailing period
  text = text.replace(/\.\s*$/, '');

  // Truncate to ~80 chars at word boundary
  if (text.length > 80) {
    text = text.substring(0, 77).replace(/\s+\S*$/, '') + '…';
  }

  return text;
}

export const GoalPipelineProgress: React.FC<GoalPipelineProgressProps> = ({
  goalId,
  goalText,
  onDismiss,
}) => {
  const navigate = useNavigate();
  const [pipelineStatus, setPipelineStatus] = useState<PipelineStatusResponse | null>(null);
  const [polling, setPolling] = useState(true);

  const onDismissRef = useRef(onDismiss);
  useEffect(() => {
    onDismissRef.current = onDismiss;
  }, [onDismiss]);

  const consecutiveFailuresRef = useRef(0);
  const failureTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const MAX_CONSECUTIVE_FAILURES = 10;

  // Cleanup failure timeout on unmount
  useEffect(() => {
    return () => {
      if (failureTimeoutRef.current) {
        clearTimeout(failureTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    console.log('🔧 PROGRESS: Component mounted', { goalId });

    const pollPipelineStatus = async () => {
      try {
        const jwtToken = await storage.getItem('jwt_token');
        const headers: Record<string, string> = {};
        if (jwtToken) {
          headers['Authorization'] = `Bearer ${jwtToken}`;
        }
        const response = await fetch(`${API_BASE_URL}/api/pipeline/status/${goalId}`, { headers });
        if (response.ok) {
          consecutiveFailuresRef.current = 0;
          const status: PipelineStatusResponse = await response.json();
          setPipelineStatus(status);

          console.log('📊 PROGRESS: Pipeline status', status.pipeline_status,
            'phases:', status.phases?.map(p => `${p.phase}:${p.status}`).join(', '));

          if (status.pipeline_status === 'completed' || status.pipeline_status === 'failed') {
            setPolling(false);
            // On failure, auto-dismiss after 4s (stored in ref for cleanup)
            if (status.pipeline_status === 'failed') {
              failureTimeoutRef.current = setTimeout(() => {
                onDismissRef.current?.();
              }, 4000);
            }
            // On success, stay visible until user clicks X or "Explore the plan"
          }
        } else {
          consecutiveFailuresRef.current += 1;
          console.error(`❌ PROGRESS: API returned ${response.status} ${response.statusText} (failure ${consecutiveFailuresRef.current}/${MAX_CONSECUTIVE_FAILURES})`);
          if (consecutiveFailuresRef.current >= MAX_CONSECUTIVE_FAILURES) {
            console.error('❌ PROGRESS: Too many consecutive failures, stopping polling and dismissing indicator');
            setPolling(false);
            onDismissRef.current?.();
          }
        }
      } catch (error) {
        consecutiveFailuresRef.current += 1;
        console.error(`❌ PROGRESS: Fetch error (failure ${consecutiveFailuresRef.current}/${MAX_CONSECUTIVE_FAILURES})`, error);
        if (consecutiveFailuresRef.current >= MAX_CONSECUTIVE_FAILURES) {
          console.error('❌ PROGRESS: Too many consecutive failures, stopping polling and dismissing indicator');
          setPolling(false);
          onDismissRef.current?.();
        }
      }
    };

    if (polling) pollPipelineStatus();

    if (polling) {
      const interval = setInterval(pollPipelineStatus, 3000);
      return () => clearInterval(interval);
    }
  }, [goalId, polling]);

  // Determine segment states (same mapping as before)
  const getSegmentState = useCallback((segmentIndex: number): SegmentState => {
    if (!pipelineStatus?.phases?.length) return segmentIndex === 0 ? 'active' : 'pending';

    const segmentPhaseNumbers: number[][] = [
      [1],        // "Understanding your goal" = Phase 1
      [2],        // "Developing your plan" = Phase 2
      [3, 4, 5],  // "Creating your first week" = Phases 3-5
    ];

    const phaseNumbers = segmentPhaseNumbers[segmentIndex];
    const segmentPhases = phaseNumbers
      .map(num => pipelineStatus.phases.find(p => p.phase === num))
      .filter(Boolean) as PhaseDetail[];

    if (segmentPhases.some(p => p.status === 'failed')) return 'failed';
    if (segmentPhases.length > 0 && segmentPhases.every(p => p.status === 'completed')) return 'completed';
    if (segmentPhases.some(p => p.status === 'in_progress' || p.status === 'retrying')) return 'active';

    if (segmentIndex > 0) {
      const prevComplete = segmentPhaseNumbers.slice(0, segmentIndex).every(prevNums =>
        prevNums.every(num => pipelineStatus.phases.find(p => p.phase === num)?.status === 'completed')
      );
      if (prevComplete && segmentPhases.some(p => p.status === 'pending')) return 'active';
    }

    return 'pending';
  }, [pipelineStatus]);

  const isComplete = pipelineStatus?.pipeline_status === 'completed';
  const isFailed = pipelineStatus?.pipeline_status === 'failed';

  // Build segment labels from backend phase titles when available,
  // falling back to defaults. Segment 2 maps to phases 3-5; use phase 3's title.
  const segmentLabels = (() => {
    if (!pipelineStatus?.phases?.length) return SEGMENT_LABELS_DEFAULT;
    const p1 = pipelineStatus.phases.find(p => p.phase === 1);
    const p2 = pipelineStatus.phases.find(p => p.phase === 2);
    const p3 = pipelineStatus.phases.find(p => p.phase === 3);
    // Show retry-specific label when Phase 3 is retrying
    const phase3Label = p3?.status === 'retrying'
      ? 'Retrying plan generation…'
      : (p3?.title || SEGMENT_LABELS_DEFAULT[2]);
    return [
      p1?.title || SEGMENT_LABELS_DEFAULT[0],
      p2?.title || SEGMENT_LABELS_DEFAULT[1],
      phase3Label,
    ];
  })();

  // Find the current active status label
  const getCurrentStatusLabel = (): string => {
    for (let i = 0; i < 3; i++) {
      const state = getSegmentState(i);
      if (state === 'active') return segmentLabels[i];
      if (state === 'failed') return segmentLabels[i];
    }
    // If all completed but pipeline not yet marked complete, show last
    return segmentLabels[2];
  };

  // Build title
  const title = isFailed
    ? 'Plan creation failed'
    : isComplete
      ? 'Plan created'
      : goalText
        ? `Creating your plan for ${cleanGoalText(goalText)}`
        : "Creating your plan";

  const handleExplorePlan = () => {
    onDismissRef.current?.();
    navigate(`/goal/${goalId}`, { state: { source: 'pipeline-complete', from: 'chat' } });
  };

  const handleDismiss = () => {
    onDismissRef.current?.();
  };

  return (
    <>
      <style>
        {`
          @keyframes goal-spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>

      <div style={{
        background: 'rgba(95, 162, 135, 0.08)',
        borderRadius: '12px',
        padding: '14px 16px',
        fontFamily: '"Inter", system-ui, -apple-system, sans-serif',
      }}>
        {/* Title row */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          <span style={{
            fontSize: '15px',
            fontWeight: 700,
            color: isFailed ? '#DC2626' : '#1F6C4F',
            letterSpacing: '-0.2px',
            flex: 1,
          }}>
            {title}
          </span>

          {/* X dismiss button (only in completed state) */}
          {isComplete && (
            <button
              onClick={handleDismiss}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
                flexShrink: 0,
              }}
              aria-label="Dismiss"
            >
              <X size={18} strokeWidth={2} style={{ color: '#5FA287' }} />
            </button>
          )}
        </div>

        {/* Dynamic status line with spinner (when pipeline is running) */}
        {!isComplete && !isFailed && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginTop: '6px',
          }}>
            <Loader2
              size={14}
              style={{ color: '#1F6C4F', animation: 'goal-spin 1s linear infinite', flexShrink: 0 }}
            />
            <span style={{
              fontSize: '13px',
              color: '#1F6C4F',
              fontWeight: 500,
            }}>
              {getCurrentStatusLabel()}…
            </span>
          </div>
        )}

        {/* Failure message */}
        {isFailed && (
          <div style={{
            fontSize: '13px',
            color: '#DC2626',
            marginTop: '6px',
          }}>
            Something went wrong. Please try creating your goal again.
          </div>
        )}

        {/* Success status */}
        {isComplete && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginTop: '6px',
          }}>
            <Check size={14} strokeWidth={3} style={{ color: '#5FA287', flexShrink: 0 }} />
            <span style={{
              fontSize: '13px',
              color: '#5FA287',
              fontWeight: 500,
            }}>
              Ready to go
            </span>
          </div>
        )}

        {/* Explore the plan button (completed state) */}
        {isComplete && (
          <div style={{ marginTop: '10px' }}>
            <button
              onClick={handleExplorePlan}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 16px',
                backgroundColor: '#1F6C4F',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'background-color 0.2s ease',
                fontFamily: 'inherit',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#164c3a'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#1F6C4F'; }}
            >
              Explore the plan
            </button>
          </div>
        )}
      </div>
    </>
  );
};
